import { DownloadSimpleIcon, PaperclipIcon, TrashIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';

import { BaseButton } from '../../../components/button/BaseButton';
import { useDownloadDocument } from '../hooks/useDownloadDocument';
import { MultiUploadForm } from './MultiUploadForm';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { useTranslation } from 'contexts/translation-context';
import { Document } from 'src/types/enums';
import {
  DOCUMENT_DIALOG_TITLE_CLASS,
  DocumentDialogFooter,
  DocumentEmptyState,
  DocumentFileRow,
} from './DocumentDialogParts';

export interface IMinimalFile {
  id: string;
  hash: string;
  name: string;
}

interface IMultiFileUploaderButtonProps {
  buttonText: string;
  title: string;
  files: IMinimalFile[];
  disableUpload?: boolean;
  accept?: string;
  // Store uploads as individual images, splitting any uploaded pdf into one
  // image per page. Used for evidence photos.
  uploadAsImages?: boolean;
  unlinkDocument?: (id: string) => Promise<void>;
  onSubmit?: (documents: Document[], close: () => void) => Promise<void>;
  /** Custom element that opens the dialog instead of the default button. */
  trigger?: React.ReactElement;
}

/** Button + dialog for a set of attachments: open or delete each file, add more. */
export const MultiFileUploaderButton: React.FC<IMultiFileUploaderButtonProps> = (props) => {
  const { buttonText, title, files, disableUpload, accept, uploadAsImages, unlinkDocument, onSubmit, trigger } = props;
  const [open, setOpen] = useState(false);
  const { downloadFile, downloadingFiles } = useDownloadDocument();
  const { i18n } = useTranslation();

  const handleSubmit = async (documents: Document[]) => {
    if (onSubmit) {
      await onSubmit(documents, () => setOpen(false));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {trigger ?? (
          <BaseButton
            color={files.length ? 'primary' : 'default-outline'}
            iconLeft={<PaperclipIcon className="button-icon" />}
          >
            {buttonText}
          </BaseButton>
        )}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title className={DOCUMENT_DIALOG_TITLE_CLASS}>{title}</Dialog.Title>

          {files.length ? (
            <div className="flex max-h-[40vh] flex-col gap-2 overflow-y-auto">
              {files.map((file, idx) => (
                <DocumentFileRow
                  key={`file-${file.id}-${idx}`}
                  name={file.name}
                  actions={
                    <>
                      <BaseButton
                        size={6}
                        iconLeft={<DownloadSimpleIcon className="h-3 w-3" />}
                        onClick={() => downloadFile(file)}
                        isLoading={downloadingFiles.includes(file.id)}
                      >
                        {i18n('download.open')}
                      </BaseButton>
                      {unlinkDocument && (
                        <ConfirmDialog
                          triggerSize={6}
                          triggerColor="danger"
                          triggerTitle={i18n('upload.deleteDocument')}
                          triggerText={<TrashIcon className="h-3 w-3" />}
                          title={i18n('upload.confirmDeleteDocument')}
                          submitText={i18n('upload.deleteDocument')}
                          description={i18n('upload.confirmDeleteDocument')}
                          onSubmit={async () => {
                            await unlinkDocument(file.id);
                          }}
                        />
                      )}
                    </>
                  }
                />
              ))}
            </div>
          ) : (
            <DocumentEmptyState text={i18n('upload.noDocumentsFound')} />
          )}

          {disableUpload ? (
            <DocumentDialogFooter>
              <Dialog.Close asChild>
                <BaseButton size={8}>{i18n('close')}</BaseButton>
              </Dialog.Close>
            </DocumentDialogFooter>
          ) : (
            <div className="mt-4">
              <div className="label-text mb-2">{i18n('upload.title')}</div>
              <MultiUploadForm
                accept={accept}
                uploadUrl={uploadAsImages ? '/api/document/upload-images' : undefined}
                multiplePerFile={uploadAsImages}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setOpen(false);
                }}
              />
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
